<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search categories..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchCategories"
      @page-change="goToPage"
    >
      <template v-if="$checkPermission('categories.create')" #actions>
        <button @click="openCreate()" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Category
        </button>
      </template>
    </ListToolbar>

    <!-- Loading skeleton -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200/60 overflow-hidden card-shadow">
      <div v-for="i in 6" :key="'skel-' + i" class="flex items-center gap-3 px-6 py-3.5 border-b border-gray-50">
        <div class="w-4 h-4 bg-gray-100 rounded skeleton-pulse shrink-0" />
        <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (30 + i * 8) + '%' }" />
      </div>
    </div>

    <!-- Tree view -->
    <div v-else-if="treeItems.length" class="bg-white rounded-xl border border-gray-200/60 overflow-hidden card-shadow">
      <CategoryTree
        :items="treeItems"
        :depth="0"
        :can-create="$checkPermission('categories.create')"
        :can-update="$checkPermission('categories.update')"
        :can-delete="$checkPermission('categories.delete')"
        @edit="openEdit"
        @delete="confirmDelete"
        @add-child="openCreateChild"
      />
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && search" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching categories found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No categories yet</p>
      <p class="text-sm text-gray-500 mt-1">Create categories to organize your content</p>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ editItem ? 'Edit Category' : 'New Category' }} <span v-if="editItem" class="text-sm font-normal text-gray-400">({{ editItem._id }})</span></h3>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="saveCategory" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                <input v-model="form.name" type="text" required placeholder="Category name" class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Description</label>
                <textarea v-model="form.description" rows="2" placeholder="Optional description" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Parent Category</label>
                <select v-model="form.parent_id">
                  <option :value="null">None (top level)</option>
                  <option v-for="cat in flatCategories" :key="cat._id" :value="cat._id" :disabled="editItem && cat._id === editItem._id">
                    {{ cat._prefix }}{{ cat.name }}
                  </option>
                </select>
              </div>
              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="btn-secondary">Cancel</button>
                <button type="submit" :disabled="saving" class="btn-primary">{{ saving ? 'Saving...' : 'Save' }}</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete modal with options -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showDeleteModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            <div class="px-6 pt-6 pb-2 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">Delete Category</h3>
              <p class="mt-2 text-sm text-gray-500">Delete "{{ deleteItem?.name }}"?</p>
            </div>

            <div class="overflow-y-auto">
            <div v-if="hasChildren" class="px-6 py-3">
              <p class="text-sm font-medium text-gray-700 mb-3">This category has subcategories. Choose what to do:</p>
              <div class="space-y-2">
                <label class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors" :class="deleteMode === 'cascade' ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'">
                  <input type="radio" v-model="deleteMode" value="cascade" class="mt-0.5 w-4 h-4 text-red-500 focus:ring-red-500" />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Delete all subcategories</p>
                    <p class="text-sm text-gray-400 mt-0.5">Permanently remove this category and all its children</p>
                  </div>
                </label>
                <label class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors" :class="deleteMode === 'move' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300'">
                  <input type="radio" v-model="deleteMode" value="move" class="mt-0.5 w-4 h-4 text-[var(--color-primary)] focus:ring-[var(--color-primary)]" />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Move children to parent</p>
                    <p class="text-sm text-gray-400 mt-0.5">Subcategories will be moved up one level</p>
                  </div>
                </label>
              </div>
            </div>

            <!-- Delete error -->
            <div v-if="deleteError" class="mx-6 mt-2 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100">
              <svg class="w-4 h-4 text-red-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <span class="text-sm text-red-600">{{ deleteError }}</span>
            </div>
            </div>

            <div class="flex justify-end gap-3 px-6 py-4 shrink-0">
              <button @click="showDeleteModal = false" :disabled="deleting" class="btn-secondary">Cancel</button>
              <button @click="deleteCategory" :disabled="deleting" class="btn-danger">
                <svg v-if="deleting" class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83"/>
                </svg>
                {{ deleting ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

// Recursive tree renderer
const CategoryTree = {
  name: 'CategoryTree',
  props: {
    items: Array,
    depth: { type: Number, default: 0 },
    canCreate: { type: Boolean, default: false },
    canUpdate: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false }
  },
  emits: ['edit', 'delete', 'add-child'],
  components: { ActionsMenu },
  methods: {
    categoryActions(item) {
      return [
        { label: 'Edit', show: this.canUpdate, action: () => this.$emit('edit', item), icon: icons.edit },
        { label: 'Add Subcategory', show: this.canCreate, action: () => this.$emit('add-child', item), icon: icons.plus },
        { divider: true },
        { label: 'Delete', show: this.canDelete, danger: true, action: () => this.$emit('delete', item), icon: icons.delete }
      ]
    }
  },
  template: `
    <div>
      <div v-for="item in items" :key="item._id">
        <div :class="['flex items-center justify-between px-6 py-3.5 hover:bg-gray-50/50 transition-colors border-b border-gray-50', depth > 0 ? 'bg-gray-50/30' : '']" :style="{ paddingLeft: (24 + depth * 24) + 'px' }">
          <div class="flex items-center gap-3 min-w-0">
            <svg v-if="item.children?.length" class="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
            </svg>
            <svg v-else class="w-4 h-4 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
            </svg>
            <div class="min-w-0">
              <p class="text-[14px] text-gray-900 truncate">{{ item.name }}</p>
              <p v-if="item.description" class="text-sm text-gray-500 truncate max-w-[400px]">{{ item.description }}</p>
              <p v-else-if="item.children?.length" class="text-sm text-gray-500">{{ item.children.length }} subcategories</p>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <ActionsMenu :items="categoryActions(item)" />
          </div>
        </div>
        <CategoryTree
          v-if="item.children?.length"
          :items="item.children"
          :depth="depth + 1"
          :can-create="canCreate"
          :can-update="canUpdate"
          :can-delete="canDelete"
          @edit="$emit('edit', $event)"
          @delete="$emit('delete', $event)"
          @add-child="$emit('add-child', $event)"
        />
      </div>
    </div>
  `
}

export default {
  name: 'CategoriesList',
  components: { ListToolbar, ConfirmModal, CategoryTree },

  data() {
    return {
      loading: true,
      items: [],
      treeItems: [],
      search: '',
      sort: 'name:1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 50, page: 1, total: 0 },
      showModal: false,
      showDeleteModal: false, deleting: false,
      deleteMode: 'cascade',
      deleteError: null,
      saving: false,
      editItem: null,
      deleteItem: null,
      form: { name: '', description: '', parent_id: null }
    }
  },

  computed: {
    hasChildren() {
      return this.deleteItem?.children?.length > 0
    },
    flatCategories() {
      const flat = []
      const flatten = (items, prefix = '') => {
        for (const item of items) {
          flat.push({ ...item, _prefix: prefix })
          if (item.children?.length) flatten(item.children, prefix + '— ')
        }
      }
      flatten(this.treeItems)
      return flat
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchCategories() },
    sort() { this.pagination.page = 1; this.fetchCategories() }
  },

  mounted() {
    this.fetchCategories()
  },

  methods: {
    async fetchCategories(count = true) {
      try {
        const params = { tree: true }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (count) params.count = true

        const res = await api.get('/categories', { params })
        this.treeItems = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch { /* keep */ } finally { this.loading = false }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchCategories(false)
    },

    openCreate() {
      this.editItem = null
      this.form = { name: '', description: '', parent_id: null }
      this.showModal = true
    },

    openCreateChild(parent) {
      this.editItem = null
      this.form = { name: '', description: '', parent_id: parent._id }
      this.showModal = true
    },

    openEdit(item) {
      this.editItem = item
      this.form = {
        name: item.name,
        description: item.description || '',
        parent_id: item.parent_id || null
      }
      this.showModal = true
    },

    async saveCategory() {
      this.saving = true
      try {
        if (this.editItem) {
          await api.put(`/categories/${this.editItem._id}`, { category: this.form })
          notifySuccess('Category updated')
        } else {
          await api.post('/categories', { category: this.form })
          notifySuccess('Category created')
        }
        this.showModal = false
        this.fetchCategories()
      } catch { /* toast */ }
      this.saving = false
    },

    confirmDelete(item) {
      this.deleteItem = item
      this.deleteMode = 'cascade'
      this.deleteError = null
      this.showDeleteModal = true
    },

    async deleteCategory() {
      if (!this.deleteItem) return
      this.deleting = true
      this.deleteError = null
      try {
        await api.delete(`/categories/${this.deleteItem._id}?mode=${this.deleteMode}`)
        notifySuccess('Category deleted')
        this.showDeleteModal = false
        this.deleteItem = null
        this.fetchCategories()
      } catch (err) {
        this.deleteError = err?.message || 'Failed to delete category'
      } finally { this.deleting = false }
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
